import React, {useState, memo, useCallback} from 'react';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import {MenuItem, Typography} from "@material-ui/core";
import AddIcon from '@material-ui/icons/Add';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import PanoramaIcon from '@material-ui/icons/Panorama';
import PermMediaIcon from '@material-ui/icons/PermMedia';
import PublicIcon from '@material-ui/icons/Public';
import {
  openDialogFile,
  openDialogFolder,
  openDialogMulti,
  useAssetTools
} from '../utilities';
import {
  modelFileFilter,
  imageCSVFilter,
  imageOrthoFilter,
  imagePlanarFilter,
  pointCloudFilter,
  tagCSVFilter,
  xmlFileFilter
} from '../file-extensions';
import {AssetFolder} from "./folder";
import {createFolder, selectDefaultFolder, selectAllFolders} from '../redux/folders-slice';
import {selectAllAssets} from '../redux/assets-slice';
import {useSelector} from 'react-redux';
import {
  TextDialog,
  DenseIcon,
  DenseDivider,
  SlimScrollbar,
  FontAwesomeIcon,
  DenseMenu
} from '../components';
import path from 'path';
import { isElectronApp } from '../electron-modules';
import {toast} from '../app';
import { useTranslation } from 'react-i18next';
import CloudIcon from "@material-ui/icons/Cloud";
import RoomIcon from "@material-ui/icons/Room";
import PanoramaOutlinedIcon from "@material-ui/icons/PanoramaOutlined";
import SatelliteIcon from "@material-ui/icons/Satellite";
import { useContextMenu, useDialog } from '../hooks';
import { WebSourceDialog } from './web-sources';
import { useAppDispatch } from '../redux/store';

interface AddAssetListProps {
  folderID: string;
  closeContextMenu(): void;
}

export const AddAssetList = (props: AddAssetListProps) => {
  const {folderID, closeContextMenu} = props;

  const {t} = useTranslation();

  const webmapDialog = useDialog();

  const {
    addModelFiles, addPointCloudFiles, addOrthoFiles,
    addCameraFiles, addPlanarFiles, addXMLFiles,
    addTagCSVs
  } = useAssetTools();

  const handleAddModels = async () => {
    closeContextMenu();

    const assetPaths = await openDialogMulti(modelFileFilter);
    if (assetPaths.length === 0) return;

    addModelFiles(assetPaths, folderID);
  };

  const handleAddPoints = async () => {
    closeContextMenu();

    const assetPaths = await openDialogMulti(pointCloudFilter);
    if (assetPaths.length === 0) return;

    addPointCloudFiles(assetPaths, folderID);
  };

  const handleAddOrtho = async () => {
    closeContextMenu();

    const assetPaths = await openDialogMulti(imageOrthoFilter);
    if (assetPaths.length === 0) return;

    addOrthoFiles(assetPaths, folderID);
  };

  const handleAddPanoramics = async () => {
    closeContextMenu();

    const assetPaths = await openDialogMulti(imageCSVFilter);
    if (assetPaths.length === 0) return;

    addCameraFiles(assetPaths, folderID);
  };

  const handleAddTags = async () => {
    closeContextMenu();

    const assetPaths = await openDialogMulti(tagCSVFilter);
    if (assetPaths.length === 0) return;

    addTagCSVs(assetPaths, folderID);
  };

  const handleAddPlanars = async () => {
    closeContextMenu();

    const assetPath = await openDialogFile(imagePlanarFilter);
    if (!assetPath) return;

    addPlanarFiles([assetPath], folderID);
  };

  const handleAddXML = async () => {
    closeContextMenu();

    const assetPath = await openDialogFile(xmlFileFilter);
    if (!assetPath) return;

    try {
      await addXMLFiles([assetPath], folderID);
      toast.success(t("toast.added-new-xml"));
    } catch {
      const fileName = path.basename(assetPath);
      toast.error(t("toast.import-xml-error", {
        path: fileName
      }));
    }
  };

  const handleAddWebMap = useCallback(() => {
    closeContextMenu();
    webmapDialog.handleOpen();
  }, []);

  return (
    <React.Fragment>
      <MenuItem onClick={handleAddPoints}>
        <DenseIcon>
          <CloudIcon fontSize="small"/>
        </DenseIcon>
        <Typography variant="inherit">{t("folder-list.add-point-cloud")}</Typography>
      </MenuItem>

      <MenuItem onClick={handleAddPanoramics}>
        <DenseIcon>
          <PanoramaIcon fontSize="small"/>
        </DenseIcon>
        <Typography variant="inherit">{t("folder-list.add-panoramic-csv")}</Typography>

      </MenuItem>

      <MenuItem onClick={handleAddTags}>
        <DenseIcon>
          <RoomIcon fontSize="small"/>
        </DenseIcon>
        <Typography variant="inherit">{t("folder-list.add-tag-csv")}</Typography>
      </MenuItem>

      <MenuItem onClick={handleAddPlanars}>
        <DenseIcon>
          <PanoramaOutlinedIcon fontSize="small"/>
        </DenseIcon>
        <Typography variant="inherit">{t("folder-list.add-planar-images")}</Typography>

      </MenuItem>

      <MenuItem onClick={handleAddOrtho}>
        <DenseIcon>
          <SatelliteIcon fontSize="small"/>
        </DenseIcon>
        <Typography variant="inherit">{t("folder-list.add-orthomosaic")}</Typography>
      </MenuItem>

      <MenuItem onClick={handleAddModels}>
        <DenseIcon>
          <FontAwesomeIcon listIcon icon={"fa-cube"}/>
        </DenseIcon>
        <Typography variant="inherit">{t("folder-list.add-model-file")}</Typography>
      </MenuItem>

      <MenuItem onClick={handleAddXML}>
        <DenseIcon>
          <FontAwesomeIcon listIcon icon={"fa-sort-amount-asc"}/>
        </DenseIcon>
        <Typography variant="inherit">{t('folder-list.add-landxml-file')}</Typography>
      </MenuItem>

      <MenuItem onClick={handleAddWebMap}>
        <DenseIcon>
          <PublicIcon fontSize="small"/>
        </DenseIcon>
        <Typography variant="inherit">{t('folder-list.add-web-source')}</Typography>
      </MenuItem>

      <WebSourceDialog
        open={webmapDialog.open}
        onClose={webmapDialog.handleClose}
        folderID={folderID}
      />
    </React.Fragment>
  );
};

export const FolderList = memo(() => {
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const contextMenu = useContextMenu();
  const newFolderDialog = useDialog();
  const {addFilePaths} = useAssetTools();

  const assets = useSelector(selectAllAssets);
  const defaultFolder = useSelector(selectDefaultFolder);
  const folders = useSelector(selectAllFolders);

  const [draggingAsset, setDraggingAsset] = useState(null);

  const handleSubmit = async (name: string) => {
    dispatch(createFolder(name));
    newFolderDialog.handleClose();
  };

  const handleAddFolder = async () => {
    contextMenu.handleClose();

    const {directoryPath, filePaths} = await openDialogFolder();
    if (filePaths.length === 0) return;

    const name = path.basename(directoryPath);
    const folderID = await dispatch(createFolder(name));
    addFilePaths(filePaths, folderID);
  };

  const handleNewFolder = () => {
    contextMenu.handleClose();
    newFolderDialog.handleOpen();
  };

  return (
    <React.Fragment>
      {/* Add item button */}
      {isElectronApp && (<React.Fragment>
        <ListItem
          button
          onClick={contextMenu.handleOpen}
          style={{
            paddingTop: "0px",
            paddingBottom: "0px"
          }}
        >
          <DenseIcon>
            <AddIcon fontSize="small"/>
          </DenseIcon>
          <ListItemText secondary={t("folder-list.add-item")}/>
        </ListItem>

        <DenseDivider variant="middle" spacing={10}/>
      </React.Fragment>)}

      <SlimScrollbar>
        <List disablePadding>
          {folders.map(folder =>
            <AssetFolder
              key={folder.id}
              folder={folder}
              assets={assets.filter(x => x.folderID === folder.id)}
              draggingAsset={draggingAsset}
              setDraggingAsset={setDraggingAsset}
            />)}
        </List>
      </SlimScrollbar>

      <TextDialog
        clear
        open={newFolderDialog.open}
        onCancel={newFolderDialog.handleClose}
        onSubmit={handleSubmit}
        title={t("folder-list.folder-prompt-title")}
        prompt={t("folder-list.folder-prompt")}
        label={t("folder-list.folder-prompt-label")}
      />

      <DenseMenu
        open={contextMenu.open}
        onClose={contextMenu.handleClose}
        anchorPosition={contextMenu.anchorPosition}
      >
        <MenuItem onClick={handleNewFolder}>
          <DenseIcon>
            <CreateNewFolderIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">{t("folder-list.create-new-folder")}</Typography>
        </MenuItem>

        <AddAssetList
          folderID={defaultFolder.id}
          closeContextMenu={contextMenu.handleClose}
        />

        <MenuItem onClick={handleAddFolder}>
          <DenseIcon>
            <PermMediaIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">{t("folder-list.add-folder-contents")}</Typography>
        </MenuItem>
      </DenseMenu>
    </React.Fragment>
  );
});
